// The three non-enabled gate visuals: locked (upsell), unavailable (service error), disabled.

import React from 'react';
import styled from 'styled-components';
import { GradientTextAction } from 'src/common/GradientText';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { AiSvg, AlertOutlineSvg } from 'src/assets/dynamicSvg';
import { ColorSet } from 'src/redux/reducers/theme';
import { RiskIntelGateState } from '../types';

const SUPPORT_EMAIL = 'support@cydea.com';

// Theme colour per tone at fill/ring opacities so the badge adapts to light/dark.
const BADGE: Record<'brand' | 'danger', { color: keyof ColorSet; fillAlpha: string; ringAlpha: string }> = {
  brand: { color: 'aliceBlue', fillAlpha: 'FF', ringAlpha: '47' },
  danger: { color: 'statusRed', fillAlpha: '33', ringAlpha: '0D' },
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 520px;
  margin: 0 auto;
  padding: 56px 24px 64px;
`;

const Circle = styled.div<{ $color: keyof ColorSet; $fillAlpha: string; $ringAlpha: string }>`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  box-sizing: border-box;
  background-clip: padding-box;
  background-color: ${({ theme, $color, $fillAlpha }) => `${theme.colors[$color]}${$fillAlpha}`};
  border: 10px solid ${({ theme, $color, $ringAlpha }) => `${theme.colors[$color]}${$ringAlpha}`};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
`;

const Body = styled(Poppins)`
  display: block;
  line-height: 1.6;
  max-width: 460px;
`;

const withEmailLink = (body: string): React.ReactNode =>
  body.split(SUPPORT_EMAIL).map((part, index, parts) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <GradientTextAction as="a" href={`mailto:${SUPPORT_EMAIL}`} px={16} weight={500} $underline target="_blank">
          {SUPPORT_EMAIL}
        </GradientTextAction>
      )}
    </React.Fragment>
  ));

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Poppins px={22} weight={600} color="prussianBlue">
    {children}
  </Poppins>
);

interface GateStatesProps {
  state: Exclude<RiskIntelGateState, 'enabled'>;
}

export const GateStates: React.FC<GateStatesProps> = ({ state }) => {
  if (state === 'locked') {
    const c = BADGE.brand;
    return (
      <Wrap>
        <Circle $color={c.color} $fillAlpha={c.fillAlpha} $ringAlpha={c.ringAlpha}>
          <AiSvg isActive style={{ width: 32, height: 32 }} />
        </Circle>
        <Spacer $px={24} />
        <Heading>Unlock our Risk Intelligence</Heading>
        <Spacer $px={12} />
        <Body px={16} color="davysGrey">
          {withEmailLink(
            'Get VIP access to our latest feature. Contact support@cydea.com to start building stellar risk narratives, aggregating frequency data sources, and leveraging impact formulas to accurately quantify your new scenario.',
          )}
        </Body>
      </Wrap>
    );
  }

  if (state === 'unavailable') {
    const c = BADGE.danger;
    return (
      <Wrap>
        <Circle $color={c.color} $fillAlpha={c.fillAlpha} $ringAlpha={c.ringAlpha}>
          <AlertOutlineSvg style={{ width: 36, height: 36 }} />
        </Circle>
        <Spacer $px={24} />
        <Heading>Risk Intel is temporarily unavailable</Heading>
        <Spacer $px={12} />
        <Body px={16} color="davysGrey">
          {withEmailLink(
            'Our automated quantification tools are pausing for repairs. You can still create your risk details manually through the other options above. For more help, contact us at support@cydea.com.',
          )}
        </Body>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Heading>Risk Intelligence is not available on your account yet</Heading>
      <Spacer $px={12} />
      <Body px={16} color="davysGrey">
        Contact the product team to get early access.
      </Body>
    </Wrap>
  );
};
